import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, AlertTriangle, FileSignature, FileCheck, ChevronRight, Inbox, Volume2, VolumeX } from "lucide-react";

const URG = {
  normal:   { label: "Normal",   color: "#3B82F6" },
  alta:     { label: "Alta",     color: "#F59E0B" },
  muy_alta: { label: "Muy alta", color: "#DC2626" },
};

const POLL_MS = 30000;
const SOUND_KEY = "legatum.comm.sound";

// Beep usando Web Audio API (no necesita asset).
function playBeep(urgent = false) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = urgent ? 880 : 660;
    g.gain.value = 0.0001;
    o.connect(g); g.connect(ctx.destination);
    const t = ctx.currentTime;
    g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
    o.start(t); o.stop(t + 0.5);
    if (urgent) {
      // segundo pitido para urgencia muy alta
      setTimeout(() => playBeep(false), 220);
    }
    setTimeout(() => ctx.close(), 700);
  } catch { /* ignore */ }
}

export default function ComunicadosAlerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) !== "off"; } catch { return true; }
  });
  const [pulseKey, setPulseKey] = useState(0);
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const soundOnRef = useRef(soundOn);

  useEffect(() => {
    soundOnRef.current = soundOn;
    try { localStorage.setItem(SOUND_KEY, soundOn ? "on" : "off"); } catch { /* ignore */ }
  }, [soundOn]);

  const fetchSummary = async (isPoll = false) => {
    try {
      const { data: d } = await api.get("/messages/dashboard-summary");
      setData(d);
      const currentIds = new Set((d.top || []).map((m) => m.id));
      if (!firstLoadRef.current && isPoll) {
        // Detectar mensajes nuevos
        const newOnes = (d.top || []).filter((m) => !seenIdsRef.current.has(m.id));
        if (newOnes.length > 0) {
          setPulseKey((k) => k + 1);
          const mostUrgent = newOnes.find((m) => m.urgencia === "muy_alta")
                          || newOnes.find((m) => m.urgencia === "alta")
                          || newOnes[0];
          const u = URG[mostUrgent.urgencia] || URG.normal;
          toast(
            (newOnes.length === 1
              ? `Nuevo comunicado: ${mostUrgent.asunto}`
              : `${newOnes.length} comunicados nuevos · último: ${mostUrgent.asunto}`),
            {
              description: `${u.label}${mostUrgent.requiere_firma ? " · requiere firma" : mostUrgent.requiere_acuse ? " · requiere acuse" : ""}`,
              duration: 6000,
              style: {
                background: u.color,
                color: "#fff",
                border: `1px solid ${u.color}`,
                boxShadow: `0 10px 30px -10px ${u.color}AA`,
              },
            }
          );
          if (soundOnRef.current) playBeep(mostUrgent.urgencia === "muy_alta");
        }
      }
      seenIdsRef.current = currentIds;
      firstLoadRef.current = false;
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchSummary(false);
    const id = setInterval(() => fetchSummary(true), POLL_MS);
    // También cuando el tab vuelve a foco
    const onVis = () => { if (document.visibilityState === "visible") fetchSummary(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !data) return null;

  const tiles = [
    { label: "Sin leer",         value: data.unread,            color: "#A03060", Icon: Inbox },
    { label: "Pendientes acuse", value: data.pendientes_acuse,  color: "#E08A1F", Icon: FileCheck },
    { label: "Firmas pendientes",value: data.pendientes_firma,  color: "#7E22CE", Icon: FileSignature },
    { label: "Urgentes activos", value: data.urgentes_activos,  color: "#DC2626", Icon: AlertTriangle, pulse: data.muy_alta > 0 },
  ];

  const hasAlerts = data.unread + data.pendientes_acuse + data.pendientes_firma + data.urgentes_activos > 0;

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-8 md:mb-10"
      data-testid="dashboard-comunicados-alerts"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[#A03060]/15 text-[#A03060] border border-[#A03060]/30">
            <Megaphone className="h-3.5 w-3.5" />
          </div>
          <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-[var(--legatum-text-muted)]">
            Comunicados internos
          </h2>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)] ml-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSoundOn((s) => !s)}
            data-testid="dashboard-comm-sound-toggle"
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)] transition-colors"
            title={soundOn ? "Silenciar avisos" : "Activar sonido"}
          >
            {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
            {soundOn ? "Sonido" : "Silencio"}
          </button>
          <Link
            to="/comunicacion"
            className="inline-flex items-center gap-1 text-xs font-bold text-[var(--legatum-accent)] hover:underline"
            data-testid="dashboard-comm-link"
          >
            Ver bandeja <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* Tiles sobrios — superficie neutra + acento de color mínimo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
        {tiles.map((t, i) => {
          const Icon = t.Icon;
          return (
            <motion.div
              key={`${t.label}-${pulseKey}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.26 }}
              whileHover={{ y: -2 }}
              className="relative rounded-xl p-4 overflow-hidden transition-colors"
              data-testid={`dashboard-comm-tile-${i}`}
              style={{
                background: "var(--legatum-surface)",
                border: "1px solid var(--legatum-border)",
              }}
            >
              {/* Acento lateral fino del color */}
              <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full"
                    style={{ background: t.color, opacity: 0.85 }} />
              <div className="relative flex items-start justify-between gap-2 pl-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--legatum-text-muted)] truncate">{t.label}</div>
                  <motion.div
                    key={t.value}
                    initial={{ scale: 1.15, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3, type: "spring", stiffness: 260, damping: 20 }}
                    className="mt-1.5 text-3xl font-extrabold tabular-nums leading-none text-[var(--legatum-text)]"
                  >
                    {t.value}
                  </motion.div>
                </div>
                <div className="relative h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: `${t.color}14`, border: `1px solid ${t.color}33` }}>
                  {t.pulse && (
                    <span className="absolute inset-0 rounded-lg animate-ping opacity-40" style={{ background: `${t.color}33` }} />
                  )}
                  <Icon className="h-4 w-4 relative" style={{ color: t.color }} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Lista top de alertas */}
      {hasAlerts && data.top.length > 0 && (
        <div className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] overflow-hidden">
          <AnimatePresence initial={false}>
            {data.top.map((msg) => {
              const u = URG[msg.urgencia] || URG.normal;
              return (
                <motion.div
                  key={msg.id}
                  layout
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  <Link
                    to="/comunicacion"
                    data-testid={`dashboard-comm-alert-${msg.id}`}
                    className="block px-4 py-3 transition-colors duration-200 hover:bg-[var(--legatum-elevated)] border-b border-[var(--legatum-border)] last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-[3px] self-stretch rounded-full shrink-0" style={{ background: u.color, opacity: 0.85 }} />
                      <div className="h-9 w-9 rounded-lg shrink-0 flex items-center justify-center"
                           style={{ background: `${u.color}14`, border: `1px solid ${u.color}33`, color: u.color }}>
                        <Megaphone className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold truncate text-[var(--legatum-text)]">{msg.asunto}</span>
                          <span style={{ background: `${u.color}1F`, color: u.color, border: `1px solid ${u.color}44` }}
                                className="rounded-md uppercase text-[9px] tracking-wider font-bold px-1.5 py-0.5">
                            {u.label}
                          </span>
                          {msg.requiere_acuse && <span className="rounded-md text-[9px] font-bold px-1.5 py-0.5 bg-[var(--legatum-elevated)] text-[var(--legatum-text-secondary)] border border-[var(--legatum-border)]">ACUSE</span>}
                          {msg.requiere_firma && <span className="rounded-md text-[9px] font-bold px-1.5 py-0.5 bg-[var(--legatum-elevated)] text-[var(--legatum-text-secondary)] border border-[var(--legatum-border)]">FIRMA</span>}
                        </div>
                        <div className="mt-0.5 text-xs text-[var(--legatum-text-muted)] font-medium">
                          De {msg.autor_nombre || "—"} · {formatDate(msg.created_at)}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[var(--legatum-text-muted)] shrink-0" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
      {!hasAlerts && (
        <div className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-6 text-center text-sm text-[var(--legatum-text-muted)]" data-testid="dashboard-comm-empty">
          ✓ Bandeja al día — no hay comunicados pendientes.
        </div>
      )}
    </motion.section>
  );
}
